import GlobalFooter from '@/components/GlobalFooter/GlobalFooter';
import GlobalMinimalFooter, {
  type GlobalMinimalFooterContent,
} from '@/components/GlobalMinimalFooter/GlobalMinimalFooter';
import GlobalMinimalHeader, {
  type GlobalMinimalHeaderContent,
} from '@/components/GlobalMinimalHeader/GlobalMinimalHeader';
import { GlobalNavigationWrapper } from '@/components/GlobalNavigation/GlobalNavigation';
import LegacyFooter from '@/components/Layout/Footer/Footer';
import LegacyHeader from '@/components/Layout/Header/Header';
import StaticSchema from '@/components/Schema/StaticSchema';
import { Section } from '@/components/Section/Section';
import SkipToContent from '@/components/SkipToContent/SkipToContent';
import type {
  Footer as FooterContent,
  GlobalFooterContent,
  GlobalNavContent,
  HeaderContent,
  StandardPageContent,
} from '@/contentful';
import type { userLocationInterface } from '@/hooks/geo';

interface Props extends userLocationInterface {
  content: StandardPageContent;
}

interface HeaderProps extends userLocationInterface {
  header: HeaderContent | GlobalNavContent;
}

interface FooterProps extends userLocationInterface {
  footer: FooterContent | GlobalFooterContent;
}

export function HeaderComponent({
  header,
  userLocation,
  setUserLocation,
}: HeaderProps) {
  const headerContentType = header?.sys?.contentType?.sys?.id;

  // biome-ignore lint/suspicious/noImplicitAnyLet: Automated migration to Biome
  let headerComponent;
  if (headerContentType) {
    if (headerContentType === 'globalMinimalHeader') {
      headerComponent = (
        <GlobalMinimalHeader
          content={header as unknown as GlobalMinimalHeaderContent}
        />
      );
    } else if (headerContentType === 'globalNav') {
      headerComponent = (
        <GlobalNavigationWrapper
          content={header as GlobalNavContent}
          userLocation={userLocation}
          setUserLocation={setUserLocation}
        />
      );
    } else {
      headerComponent = <LegacyHeader content={header as HeaderContent} />;
    }
  }

  return headerComponent || null;
}

export function FooterComponent({
  footer,
  userLocation,
  setUserLocation,
}: FooterProps) {
  const footerContentType = footer?.sys?.contentType?.sys?.id;

  // biome-ignore lint/suspicious/noImplicitAnyLet: Automated migration to Biome
  let footerComponent;

  if (footerContentType) {
    if (footerContentType === 'globalMinimalFooter') {
      footerComponent = (
        <GlobalMinimalFooter
          content={footer as unknown as GlobalMinimalFooterContent}
        />
      );
    } else if (footerContentType === 'globalFooter') {
      footerComponent = (
        <GlobalFooter
          content={footer as GlobalFooterContent}
          userLocation={userLocation}
          setUserLocation={setUserLocation}
        />
      );
    } else {
      footerComponent = <LegacyFooter content={footer as FooterContent} />;
    }
  }

  return footerComponent || null;
}

export default function StandardPage({
  content: {
    fields: { header, footer, sections },
  },
  userLocation,
  setUserLocation,
}: Props) {
  return (
    <>
      <StaticSchema />
      <SkipToContent href='#main' />
      <HeaderComponent
        header={header}
        userLocation={userLocation}
        setUserLocation={setUserLocation}
      />
      {/** biome-ignore lint/correctness/useUniqueElementIds: Automated migration to Biome */}
      <main style={{ backgroundColor: 'black' }} id='main'>
        {sections.map((section, index) => (
          <Section
            key={section.sys.id}
            section={section}
            userLocation={userLocation}
            sectionPosition={index}
          />
        ))}
      </main>
      <FooterComponent
        footer={footer}
        userLocation={userLocation}
        setUserLocation={setUserLocation}
      />
    </>
  );
}
